import React, { useState, useRef } from 'react';
import {
  Box, Heading, Text, Button, VStack, HStack, useToast, Icon,
  Card, CardBody, Alert, AlertIcon, Table, Thead, Tbody, Tr, Th, Td,
  Badge, Progress, Code, Flex, Link,
} from '@chakra-ui/react';
import { FaUpload, FaDownload, FaCheck, FaTimesCircle } from 'react-icons/fa';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

const CSV_TEMPLATE = `patient_name,patient_dob,patient_email,patient_gender,doctor_name,doctor_phone,insurance_company,insurance_policy_number,special_instructions
"Doe, Jane",1945-03-15,jane@email.com,Female,"Dr. Smith","(555) 123-4567","Blue Cross","BC123456","Fasting required"
"Smith, Robert",1950-08-22,bob@email.com,Male,"Dr. Johnson","(555) 987-6543","Aetna","AE789012",""`;

export default function FacilityBulkUpload() {
  const { facility, facilityId } = useOutletContext();
  const toast = useToast();
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState({ success: 0, failed: 0 });

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'blood_draw_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim());
    if (lines.length < 2) return { rows: [], errors: ['CSV must have a header row and at least one data row'] };

    // Parse a CSV line respecting quoted fields
    const parseLine = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuotes) {
          if (ch === '"' && line[i + 1] === '"') { current += '"'; i++; }
          else if (ch === '"') { inQuotes = false; }
          else { current += ch; }
        } else {
          if (ch === '"') { inQuotes = true; }
          else if (ch === ',') { result.push(current.trim()); current = ''; }
          else { current += ch; }
        }
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseLine(lines[0]);
    const required = ['patient_name'];
    const missing = required.filter(r => !headers.includes(r));
    if (missing.length) return { rows: [], errors: [`Missing required columns: ${missing.join(', ')}`] };

    const parsed = [];
    const errs = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseLine(lines[i]);
      const row = {};
      headers.forEach((h, idx) => { row[h] = values[idx] || ''; });
      if (!row.patient_name?.trim()) { errs.push(`Row ${i + 1}: Missing patient name`); continue; }
      parsed.push(row);
    }
    return { rows: parsed, errors: errs };
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const { rows: parsed, errors: parseErrors } = parseCSV(event.target.result);
      setRows(parsed);
      setErrors(parseErrors);
      setUploaded(false);
      setResults({ success: 0, failed: 0 });
      if (parseErrors.length) {
        toast({ title: `${parseErrors.length} validation error(s)`, status: 'warning', duration: 3000 });
      } else {
        toast({ title: `${parsed.length} row(s) parsed`, status: 'success', duration: 2000 });
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (rows.length === 0) return;
    setUploading(true);
    setProgress(0);
    let success = 0;
    let failed = 0;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      try {
        const drawCode = Math.floor(1000 + Math.random() * 9000).toString();
        const { error } = await supabase.from('submissions').insert({
          patient_name: row.patient_name?.trim(),
          patient_email: row.patient_email?.trim() || null,
          patient_dob: row.patient_dob?.trim() || null,
          patient_gender: row.patient_gender?.trim() || null,
          doctor_name: row.doctor_name?.trim() || null,
          doctor_phone: row.doctor_phone?.trim() || null,
          insurance_company: row.insurance_company?.trim() || null,
          insurance_policy_number: row.insurance_policy_number?.trim() || null,
          special_instructions: row.special_instructions?.trim() || null,
          is_self_pay: !row.insurance_company?.trim(),
          facility_id: facilityId,
          client_name: facility?.name || null,
          draw_code: drawCode,
          status: 'pending',
          submitted_at: new Date().toISOString(),
        });
        if (error) throw error;
        success++;
      } catch (err) {
        failed++;
        console.error(`Row ${i + 1} failed:`, err);
      }
      setProgress(Math.round(((i + 1) / rows.length) * 100));
    }

    setResults({ success, failed });
    setUploaded(true);
    setUploading(false);
    toast({
      title: 'Upload complete',
      description: `${success} created, ${failed} failed`,
      status: failed === 0 ? 'success' : 'warning',
      duration: 5000,
    });
  };

  return (
    <Box>
      <Heading size="lg" fontWeight="700" mb={1}>Bulk Upload</Heading>
      <Text color="gray.500" fontSize="sm" mb={6}>Upload multiple blood draw orders at once via CSV</Text>

      {/* Instructions */}
      <Card bg="white" shadow="sm" mb={6}>
        <CardBody>
          <VStack spacing={4} align="start">
            <Heading size="sm" color="teal.600">How It Works</Heading>
            <Text fontSize="sm" color="gray.600">
              1. Download the CSV template below<br />
              2. Fill in patient and doctor information (one row per blood draw)<br />
              3. Upload the completed CSV file<br />
              4. Review the preview and click "Upload All"
            </Text>
            <Button leftIcon={<FaDownload />} colorScheme="teal" variant="outline" size="sm" onClick={downloadTemplate}>
              Download CSV Template
            </Button>
          </VStack>
        </CardBody>
      </Card>

      {/* File Upload */}
      <Card bg="white" shadow="sm" mb={6}>
        <CardBody>
          <VStack spacing={4}>
            <input type="file" accept=".csv" ref={fileRef} onChange={handleFileSelect} style={{ display: 'none' }} />
            <Button leftIcon={<FaUpload />} colorScheme="teal" size="lg" onClick={() => fileRef.current?.click()} w="full" maxW="400px">
              Select CSV File
            </Button>

            {rows.length > 0 && (
              <Alert status="info" borderRadius="md" fontSize="sm">
                <AlertIcon />
                {rows.length} row(s) ready to upload
              </Alert>
            )}

            {errors.length > 0 && (
              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                <VStack align="start" spacing={0}>
                  {errors.map((err, i) => <Text key={i}>{err}</Text>)}
                </VStack>
              </Alert>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Preview */}
      {rows.length > 0 && (
        <Card bg="white" shadow="sm" mb={6}>
          <CardBody>
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size="sm">Preview ({rows.length} rows)</Heading>
              {!uploaded && (
                <Button colorScheme="teal" leftIcon={<FaUpload />} onClick={handleUpload} isLoading={uploading} loadingText="Uploading...">
                  Upload All ({rows.length})
                </Button>
              )}
            </Flex>

            {uploading && <Progress value={progress} colorScheme="teal" size="sm" borderRadius="full" mb={4} />}

            {uploaded && (
              <Alert status={results.failed === 0 ? 'success' : 'warning'} borderRadius="md" mb={4}>
                <AlertIcon />
                {results.success} submitted successfully{results.failed > 0 ? `, ${results.failed} failed` : ''}
              </Alert>
            )}

            <Box overflowX="auto" maxH="400px" overflowY="auto">
              <Table size="sm">
                <Thead position="sticky" top={0} bg="gray.50" zIndex={1}>
                  <Tr>
                    <Th py={2}>#</Th>
                    <Th py={2}>Patient</Th>
                    <Th py={2}>DOB</Th>
                    <Th py={2}>Doctor</Th>
                    <Th py={2}>Insurance</Th>
                    <Th py={2}>Instructions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.map((row, i) => (
                    <Tr key={i}>
                      <Td fontSize="xs" color="gray.400">{i + 1}</Td>
                      <Td fontWeight="500" fontSize="sm">{row.patient_name}</Td>
                      <Td fontSize="sm">{row.patient_dob || '—'}</Td>
                      <Td fontSize="sm">{row.doctor_name || '—'}</Td>
                      <Td fontSize="sm">{row.insurance_company || <Badge colorScheme="green" fontSize="xs">Self Pay</Badge>}</Td>
                      <Td fontSize="xs" color="gray.500" maxW="200px" isTruncated>{row.special_instructions || '—'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>
      )}
    </Box>
  );
}
